import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { brand } from '../../theme';

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Right-aligned action area: one primary button, optionally secondary ones before it. */
  action?: React.ReactNode;
  /** Small label above the title, e.g. the section a detail page belongs to. */
  eyebrow?: string;
  /** A back link above the title, for detail pages. */
  backTo?: { to: string; label: string };
  /** One line of facts under the subtitle (status, owner, dates). */
  meta?: React.ReactNode;
}

/**
 * Shared page header: one consistent title, subtitle and action band at the
 * top of every screen so pages share the same vertical rhythm.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, action, eyebrow, backTo, meta }) => (
  <Box sx={{ mb: 4 }}>
    {backTo && (
      <Link
        component={RouterLink}
        to={backTo.to}
        underline="hover"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1.5,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'text.secondary',
          '&:hover': { color: 'text.primary' },
        }}
      >
        <ArrowBackOutlinedIcon sx={{ fontSize: 18 }} />
        {backTo.label}
      </Link>
    )}
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        pb: 2.5,
        borderBottom: `1px solid ${brand.border}`,
      }}
    >
      <Box sx={{ position: 'relative', pl: 2, minWidth: 0 }}>
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: 0,
            top: 3,
            bottom: 3,
            width: 3,
            borderRadius: 2,
            backgroundColor: brand.copper,
          }}
        />
        {eyebrow && (
          <Typography
            variant="caption"
            component="p"
            sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.02em', mb: 0.25 }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h4" component="h1" sx={{ color: 'text.primary', mb: subtitle || meta ? 0.55 : 0, overflowWrap: 'anywhere' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680 }}>
            {subtitle}
          </Typography>
        )}
        {meta && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 1.5,
              rowGap: 0.75,
              mt: subtitle ? 1 : 0.25,
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            {meta}
          </Box>
        )}
      </Box>
      {action && (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>{action}</Box>
      )}
    </Box>
  </Box>
);

export default PageHeader;
